<template>
  <div class="contact-page">

    <section class="contact-hero">
      <div class="contact-hero-inner">
        <h1 class="contact-h1">Get in touch.</h1>
        <p class="contact-hero-sub">
          We're a community — not a corporation. Reach out however feels right.
        </p>
      </div>
    </section>

    <section class="contact-body">
      <div class="contact-grid">

        <!-- Slack -->
        <div class="contact-item">
          <p class="contact-item-label">Community</p>
          <h2 class="contact-item-title">Join us on Slack</h2>
          <p class="contact-item-body">
            Become part of a vibrant network of people passionate about technology. 
            Access resources, opportunities, and a community that actually shows up for you.
          </p>
          <a class="contact-link" href="https://forms.gle/nivXcK3MwJ8ZeFRp7" target="_blank">
            Join the community →
          </a>
        </div>

        <!-- Social -->
        <div class="contact-item contact-item--warm">
          <p class="contact-item-label">Follow along</p>
          <h2 class="contact-item-title">Social media</h2>
          <p class="contact-item-body">
            We post about programs, events, opportunities, and community wins across all our channels.
          </p>
          <div class="social-links">
            <a class="social-link" href="https://twitter.com/nexascalehq" target="_blank">
              <font-awesome-icon icon="fa-brands fa-twitter" />
              <span>Twitter / X</span>
              <span class="social-handle">@NexaScaleHQ</span>
            </a>
            <a class="social-link" href="https://instagram.com/nexascale" target="_blank">
              <font-awesome-icon icon="fa-brands fa-instagram" />
              <span>Instagram</span>
              <span class="social-handle">@NexaScale</span>
            </a>
            <a class="social-link" href="https://www.linkedin.com/company/nexascale" target="_blank">
              <font-awesome-icon icon="fa-brands fa-linkedin" />
              <span>LinkedIn</span>
              <span class="social-handle">@NexaScale</span>
            </a>
            <a class="social-link" href="https://facebook.com/nexascale/" target="_blank">
              <font-awesome-icon icon="fa-brands fa-facebook" />
              <span>Facebook</span>
              <span class="social-handle">@NexaScale</span>
            </a>
          </div>
        </div>

        <!-- Email -->
        <div class="contact-item contact-item--dark">
          <p class="contact-item-label contact-item-label--light">Say hello</p>
          <h2 class="contact-item-title contact-item-title--light">Send us an email</h2>
          <p class="contact-item-body contact-item-body--light">
            Ideas, collaborations, partnerships, questions — we read everything and reply as soon as we can.
          </p>
          <a class="contact-link contact-link--yellow" href="mailto:info@nexascale.org">
            info@nexascale.org →
          </a>
        </div>

      </div>
    </section>

  </div>
</template>

<style scoped>
.contact-page {
  min-height: var(--pageheight, 100vh);
  margin-top: 80px;
  text-align: left;
}
.contact-page * { box-sizing: border-box; }
.contact-page p, .contact-page h1, .contact-page h2 { text-align: left; }

/* HERO */
.contact-hero {
  background: #191616;
  padding: 100px 7% 80px;
}
.contact-hero-inner { max-width: 560px; }
.contact-h1 {
  font-size: clamp(3.2rem, 8vw, 7rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.0;
  color: #ffffff;
  margin: 0 0 20px;
  word-break: break-word;
}
.contact-hero-sub {
  font-size: 1rem;
  line-height: 1.75;
  color: rgba(255,255,255,0.5);
  margin: 0;
}

/* BODY */
.contact-body {
  padding: 0;
}
.contact-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  min-height: 520px;
}

/* ITEMS */
.contact-item {
  padding: 60px 52px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-right: 1px solid #e8e4e0;
  background: #ffffff;
}
.contact-item:last-child { border-right: none; }
.contact-item--warm { background: #FFFBEB; }
.contact-item--dark { background: #2C2C2C; border-right: none; }

.contact-item-label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #FFB812;
  margin: 0;
}
.contact-item-label--light { color: rgba(255,184,18,0.7); }

.contact-item-title {
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: #191616;
  margin: 0;
  word-break: break-word;
}
.contact-item-title--light { color: #ffffff; }

.contact-item-body {
  font-size: 0.93rem;
  line-height: 1.75;
  color: #666260;
  margin: 0;
  flex: 1;
}
.contact-item-body--light { color: rgba(255,255,255,0.55); }

.contact-link {
  display: inline-block;
  font-size: 0.88rem;
  font-weight: 700;
  color: #191616;
  text-decoration: none;
  border-bottom: 2px solid #FFB812;
  padding-bottom: 2px;
  align-self: flex-start;
  transition: color 0.2s;
}
.contact-link:hover { color: #FF9914; }
.contact-link--yellow {
  color: #FFB812;
  border-bottom-color: rgba(255,184,18,0.3);
}
.contact-link--yellow:hover { color: #ffffff; border-bottom-color: #ffffff; }

/* SOCIAL LINKS */
.social-links {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
}
.social-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #e8e4e0;
  text-decoration: none;
  color: #191616;
  font-size: 0.88rem;
  font-weight: 600;
  transition: color 0.15s;
}
.social-link:last-child { border-bottom: none; }
.social-link:hover { color: #FF9914; }
.social-link svg {
  width: 16px;
  flex-shrink: 0;
  color: #aaa8a4;
}
.social-link:hover svg { color: #FF9914; }
.social-handle {
  margin-left: auto;
  font-size: 0.75rem;
  font-weight: 500;
  color: #aaa8a4;
}
.social-link:hover .social-handle { color: #FF9914; }

/* RESPONSIVE */
@media (max-width: 900px) {
  .contact-grid {
    grid-template-columns: 1fr;
    min-height: unset;
  }
  .contact-item {
    border-right: none;
    border-bottom: 1px solid #e8e4e0;
    padding: 48px 5%;
  }
  .contact-item--dark { border-bottom: none; }
  .contact-hero { padding: 80px 5% 60px; }
}
</style>